import { useMemo } from 'react';
import styles from './styles.module.scss';
import { Icon } from '@components/icons';
import Typography from '@components/common/typography';
import Button from '@components/common/button';
import { useNavigate } from 'react-router-dom';
import { routes } from '@const/routes';
import { useUserContext } from '@hooks/context/useUserContext';
import { useLocales } from '@hooks/helpers/useLocales';
import { fixedNumbers, getLoyaltyLevelString } from '@utils/index';
import { ELoyaltyLevel } from '@xeppt/xeppt-sdk/types/loyalty';

const LoyaltySection = () => {
    const navigate = useNavigate();
    const { loyalty } = useUserContext();
    const { submitLocale } = useLocales();

    const getProgressPercent = useMemo(() => {
        if ((loyalty?.points || 0) <= (loyalty?.[`points_to_${ELoyaltyLevel.B}`] || 0)) {
            return Math.min(
                ((loyalty?.points || 0) / (loyalty?.[`points_to_${ELoyaltyLevel.B}`] || 0)) * 50,
                100
            );
        } else {
            return (
                Math.min(
                    (((loyalty?.points || 0) - (loyalty?.[`points_to_${ELoyaltyLevel.B}`] || 0)) /
                        ((loyalty?.[`points_to_${ELoyaltyLevel.C}`] || 0) -
                            (loyalty?.[`points_to_${ELoyaltyLevel.B}`] || 0))) *
                        50,
                    100
                ) + 50
            );
        }
    }, [loyalty]);

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <Typography variant="h5">XEPPT Momentum</Typography>
                <Button
                    variant="text-gray"
                    leftIcon="external_link"
                    iconSize={18}
                    onClick={() => window.open('https://xeppt.com/momentum', '_blank')}
                    className={styles.link}>
                    Get info
                </Button>
            </div>
            <div className={styles.balance_wrapper}>
                <div className={styles.balance}>
                    <div className={styles.info}>
                        <Icon name="tier" />
                        <Typography variant="body4">Profile level</Typography>
                    </div>
                    <Typography variant="body3" weight="semibold">
                        {getLoyaltyLevelString(loyalty?.level || ELoyaltyLevel.A)}
                    </Typography>
                </div>
                <div className={styles.balance}>
                    <div className={styles.info}>
                        <Icon name="coins" />
                        <Typography variant="body4">Points balance</Typography>
                    </div>
                    <Typography variant="body3" weight="semibold">
                        {fixedNumbers(loyalty?.points)}
                    </Typography>
                </div>
            </div>
            <div className={styles.levels_wrapper}>
                <div className={styles.level}>
                    <Typography
                        className={loyalty?.level === ELoyaltyLevel.A ? styles.isActive : ''}
                        variant="caption1">
                        Silver
                    </Typography>
                    <div
                        className={`${styles.point} ${styles.isChecked} ${loyalty?.level === ELoyaltyLevel.A ? styles.isActive : ''}`}>
                        <div />
                    </div>
                    <Typography variant="caption1">0</Typography>
                </div>
                <div className={styles.level}>
                    <Typography
                        className={loyalty?.level === ELoyaltyLevel.B ? styles.isActive : ''}
                        variant="caption1">
                        Gold
                    </Typography>
                    <div
                        className={`${styles.point} ${(loyalty?.points || 0) >= (loyalty?.[`points_to_${ELoyaltyLevel.C}`] || 0) ? styles.isChecked : ''} ${loyalty?.level === ELoyaltyLevel.B ? styles.isActive : ''}`}>
                        <div />
                    </div>
                    <Typography variant="caption1">
                        {loyalty?.[`points_to_${ELoyaltyLevel.B}`]}
                    </Typography>
                </div>
                <div className={styles.level}>
                    <Typography
                        className={loyalty?.level === ELoyaltyLevel.C ? styles.isActive : ''}
                        variant="caption1">
                        Platinum
                    </Typography>
                    <div
                        className={`${styles.point} ${(loyalty?.points || 0) >= (loyalty?.[`points_to_${ELoyaltyLevel.C}`] || 0) ? styles.isChecked : ''} ${loyalty?.level === ELoyaltyLevel.C ? styles.isActive : ''}`}>
                        <div />
                    </div>
                    <Typography variant="caption1">
                        {loyalty?.[`points_to_${ELoyaltyLevel.C}`]}
                    </Typography>
                </div>
                <div className={styles.progress}>
                    <span
                        style={{
                            width: `${getProgressPercent >= 100 ? 100 : getProgressPercent || 0}%`
                        }}
                    />
                </div>
            </div>
            <div className={styles.footer_wrapper}>
                <Button
                    variant="gold"
                    size="medium"
                    onClick={() => navigate(routes.my_loyalty_points)}>
                    {submitLocale('my_points')}
                </Button>
            </div>
        </div>
    );
};

export default LoyaltySection;
